import { z } from 'zod';
import { isCalendarDate } from '@shared/time';
import { formatPhone, isValidPhone, PHONE_EXAMPLE } from '@shared/phone';

/**
 * Reusable validation primitives.
 *
 * Money is ALWAYS integer cents. These schemas reject floats and decimal
 * strings outright — a value like 12.5 or "12.50" must never reach the
 * database.
 */

/** UUIDv4 string. */
export const uuidSchema = z.string().uuid({ message: 'Enter a valid identifier' });

/**
 * Non-negative integer cents. Use for prices, totals, and payments.
 * Rejects floats, decimal strings, and negatives.
 */
export const centsSchema = z
  .number({ message: 'Enter a dollar amount in cents' })
  .int({ message: 'Amounts must be whole cents' })
  .nonnegative({ message: 'Amount must not be negative' })
  .safe({ message: 'Amount is too large' });

/**
 * Integer cents that may be negative — for adjustments that can move a
 * balance up or down.
 */
export const signedCentsSchema = z
  .number({ message: 'Enter a dollar amount in cents' })
  .int({ message: 'Amounts must be whole cents' })
  .safe({ message: 'Amount is too large' });

/** YYYY-MM-DD calendar date, validated for real calendar existence. */
export const calendarDateSchema = z
  .string({ message: 'Enter a date' })
  .refine(isCalendarDate, { message: 'Enter a date like 2026-07-27' });

/** UTC ISO-8601 instant, e.g. "2026-07-27T16:30:00.000Z". */
export const isoInstantSchema = z
  .string({ message: 'Enter a timestamp' })
  .refine((val) => !Number.isNaN(Date.parse(val)) && val.endsWith('Z'), {
    message: 'Enter a valid UTC timestamp',
  });

/** 24-hour time of day, "HH:MM". */
export const timeOfDaySchema = z
  .string({ message: 'Enter a time' })
  .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, { message: 'Enter a time like 14:30' });

/** Basic email shape. */
export const emailSchema = z
  .string({ message: 'Enter an email address' })
  .email({ message: 'Enter a valid email address' });

/**
 * An optional US phone number, NORMALISED on the way in.
 *
 * Forgiving about what is typed and strict about what is kept: every accepted
 * spelling of the same telephone — `5045550199`, `504.555.0199`,
 * `+1 504 555 0199` — is stored as `(504) 555-0199`. See `shared/phone.ts` for
 * why, and for the numbering-plan rule that separates a real number from ten
 * digits.
 *
 * This used to store the raw value as given, so the same customer entered twice
 * in two styles produced two different strings — invisible to any search or
 * comparison. Normalising here means the rule holds for every caller, including
 * ones that never touch the form.
 *
 * An empty string means "no phone on file" and becomes undefined, so the column
 * holds NULL rather than ''. That mirrors `optionalEmail` and `optionalText`.
 */
export const phoneSchema = z
  .string()
  .transform((value) => value.trim())
  .refine((value) => value === '' || isValidPhone(value), {
    message: `Enter a phone number like ${PHONE_EXAMPLE}`,
  })
  .transform((value) => (value === '' ? undefined : (formatPhone(value) ?? value)))
  .optional();

/**
 * An email address that may legitimately be absent.
 *
 * `emailSchema.optional()` is NOT this, and the difference was a live bug.
 * `.optional()` admits `undefined` and nothing else, but an HTML form always
 * sends a string — a blank box arrives as `''`, which `.email()` rejects as
 * malformed. So the add-customer form, which marks nothing as required and
 * always sends all five fields, could not create a customer unless an email was
 * filled in. The 400 it produced said "Enter a customer name."
 *
 * This mirrors `phoneSchema` above, which has always accepted `''`, and
 * `optionalText` below, which maps `''` to undefined so the column stores NULL
 * rather than an empty string. Validity is delegated to `emailSchema` so there
 * is still exactly one definition of what a valid address looks like.
 */
export const optionalEmail = z
  .string({ message: 'Enter an email address' })
  .trim()
  .refine((s) => s === '' || emailSchema.safeParse(s).success, {
    message: 'Enter a valid email address',
  })
  .transform((s) => (s === '' ? undefined : s))
  .optional();

/** A string that is not empty after trimming. */
export const nonEmptyString = z
  .string({ message: 'This field is required' })
  .min(1, { message: 'This field is required' })
  .transform((s) => s.trim())
  .refine((s) => s.length > 0, { message: 'This field is required' });

/**
 * Optional text: trims, and converts empty string to undefined so the
 * database stores NULL rather than ''.
 */
export const optionalText = z
  .string()
  .transform((s) => {
    const trimmed = s.trim();
    return trimmed === '' ? undefined : trimmed;
  })
  .optional();

/** Pagination parameters. */
export const paginationSchema = z.object({
  limit: z
    .number({ message: 'Enter a number of results' })
    .int({ message: 'Enter a whole number' })
    .min(1, { message: 'Show at least 1 result' })
    .max(100, { message: 'Show at most 100 results' })
    .default(50),
  cursor: z.string().optional(),
});
